import { addDays } from '@if/core/date'
import { EOriginSystemId, EPaymentFrequency, EPaymentPlan, EWorksheetStatus } from '@if/core/types'
import { formatCurrency, formatDate } from '@if/core/utils'
import { Edit as EditIcon } from '@mui/icons-material'
import CarRepairIcon from '@mui/icons-material/CarRepair'
import { Box, Button, ButtonGroup, Divider, Link, Paper, Stack, Typography } from '@mui/material'
import { useCreditRslaTerms } from '@src/containers/EditWorksheet/editWorksheet-hooks'
import creditRslaWorksheetEffects from '@src/data/store/CreditRslaWorksheet/credit-rsla-worksheet-effects'
import { useSideEffect } from '@src/data/store/effects/side-effects'
import { Constants, CreditApplication, User, VehicleSchema } from '@src/data/types'
import { CreditRslaWorksheet } from '@src/data/types/CreditRslaWorksheet'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

interface Props {
  creditApplication: CreditApplication
  worksheet: CreditRslaWorksheet
  user: User | null
  editDisabled: boolean
}

const CreditRslaWorksheetInformationSection = ({ worksheet, creditApplication, user, editDisabled }: Props) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const dispatchEffect = useSideEffect()
  const availableTerms = useCreditRslaTerms(creditApplication)

  const maxAvailableTerm = Math.max(...availableTerms)
  const editWorksheetDisabled =
    creditApplication?.originSystemId !== EOriginSystemId.Backoffice || !user?.rights.canEditWorksheet || editDisabled
  // event handlers
  const createWorksheet = useCallback(() => {
    const route = `/Applications/credit-rsla/${creditApplication.id}/worksheet/edit`

    if (worksheet === null) {
      const newWorksheet = {
        id: creditApplication.id,
        status: EWorksheetStatus.Draft,
        vehicle: VehicleSchema.getDefault(),
        amountRequested: creditApplication.requestedLoanAmount,
        deliveryOn: formatDate(new Date()),
        firstPaymentOn: formatDate(addDays(new Date(), 30)),
        paymentFrequency: EPaymentFrequency.Monthly,
        paymentAmountForFrequency: 0,
        term: maxAvailableTerm,
        includeInsurance: false,
        addFastPayments: false,
        versionTag: undefined,
        fundConfirmationNote: null,
        lienAmountFee: Constants.financingProgramConfigs[creditApplication.financingProgramId].lienAmountFee,
        brokerAdminFee: 0,
        roadsideAssistanceAmount: 0,
        merchantReferralFee: 0,
        paymentPlanId: EPaymentPlan.RegularDailyInterests,
        creditApplicationId: creditApplication.id,
        insuranceFee: 0,
        planName: creditApplication.applicant.creditDecision?.planName,
        lenderFee: 0,
      }

      dispatchEffect(creditRslaWorksheetEffects.create(newWorksheet))
        .then(() => navigate(route))
        .catch(reportErrorToConsole)
    } else {
      navigate(route)
    }
  }, [worksheet, creditApplication, dispatchEffect, navigate, maxAvailableTerm])
  return (
    <Paper elevation={3} sx={{ borderRadius: 2, p: 2, mb: 3 }}>
      <Box sx={{ m: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={2}>
            <ButtonGroup variant="outlined" size="large">
              <Button id="btnEditWorksheet" component={Link} variant="outlined" disabled>
                {t('worksheet.financing')}
              </Button>
              <Button onClick={createWorksheet} disabled={editWorksheetDisabled} size="small">
                <EditIcon fontSize="large" />
              </Button>
            </ButtonGroup>
            <CarRepairIcon fontSize="large" color="primary" />
          </Stack>
        </Stack>
        <Divider />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems="top"
          marginTop={1}
          justifyContent="space-evenly"
          divider={<Divider orientation="vertical" flexItem />}
        >
          <span>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.vin')} : {worksheet?.vehicle?.vin}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.vehicle')} : {worksheet?.vehicle?.make} {worksheet?.vehicle?.model}{' '}
              {worksheet?.vehicle?.series} {worksheet?.vehicle?.year}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.mileage')} : {worksheet?.vehicle?.mileage}{' '}
              {worksheet?.vehicle?.mileage !== undefined && 'KM'}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              CBB :
              {worksheet?.vehicle?.thirdPartyEvaluationValue
                ? formatCurrency(worksheet?.vehicle?.thirdPartyEvaluationValue)
                : 'N/A'}
            </Typography>
            <Typography variant="subtitle1">
              {t('worksheet.merchantEstimatedValue')} :{' '}
              {worksheet?.vehicle?.brokerEstimatedValue
                ? formatCurrency(worksheet?.vehicle?.brokerEstimatedValue)
                : 'N/A'}
            </Typography>
          </span>
          <span>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.paymentFrequency')} :{' '}
              {worksheet && t(`EPaymentFrequency.${worksheet?.paymentFrequency}`, { ns: 'enum' })}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.term')} : {worksheet?.term} {worksheet?.term !== undefined && t('worksheet.month')}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.paymentAmount')} : {formatCurrency(worksheet?.paymentAmountForFrequency)}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.plan')} :{' '}
              {worksheet?.paymentPlanId ? t(`EPaymentPlan.${worksheet?.paymentPlanId}`, { ns: 'enum' }) : ''}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.deliveryOn')} : {formatDate(worksheet?.deliveryOn)}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.firstPaymentOn')} : {formatDate(worksheet?.firstPaymentOn)}
            </Typography>
          </span>
          <span>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheetCommon.amountRequested')} : {formatCurrency(worksheet?.amountRequested)}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.brokerAdminFee')} : {formatCurrency(worksheet?.brokerAdminFee)}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.lenderAdminFee')} : {formatCurrency(worksheet?.lenderFee)}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.ppsa')} : {formatCurrency(worksheet?.lienAmountFee)}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.roadside')} : {formatCurrency(worksheet?.roadsideAssistanceAmount)}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.merchantReferralFee')} : {formatCurrency(worksheet?.merchantReferralFee)}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.protectionPlan')} : {formatCurrency(worksheet?.insuranceFee)}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.totalAmountFinanced')} :{' '}
              {formatCurrency(worksheet?.totalAmountFinanced + worksheet?.insuranceFee)}
            </Typography>
          </span>
        </Stack>
      </Box>
    </Paper>
  )
}

export default CreditRslaWorksheetInformationSection
